import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { ToastrService } from 'ngx-toastr';
import {
  formatUserMessageText,
  isPdfStatusGenerating,
  logOrderDevDetail,
  OrderPdfStatus,
  OrderService,
  ORDER_PDF_POLL_INTERVAL_MS,
  resolveOrderGraphqlUserMessage,
  resolveUserMessage,
  triggerAttachmentDownload,
  UserMessage,
  UserMessageAction
} from 'src/entities/order';
import { OrderPdfDownloadUrlResult } from 'src/entities/order/model/order-pdf.model';
import { normalizePdfStatus } from 'src/entities/order/model/order-status.model';

export interface OrderPdfDialogData {
  orderId: string;
  orderNumber?: string | number | null;
  pdfStatus?: string | null;
  lastPdfError?: string | null;
}

export interface OrderPdfDialogResult {
  pdfStatus?: string;
  pdfGeneratedAt?: string | null;
  lastPdfError?: string | null;
}

type ViewState = 'loading' | 'generating' | 'view' | 'error';

@Component({
  selector: 'app-order-pdf-dialog',
  templateUrl: './order-pdf-dialog.component.html',
  styleUrls: ['./order-pdf-dialog.component.css'],
  host: { class: 'order-pdf-dialog-host' }
})
export class OrderPdfDialogComponent implements OnInit, OnDestroy {
  state: ViewState = 'loading';
  errorTitle: string | null = null;
  errorMessage: string | null = null;
  errorAction: UserMessageAction = 'none';
  downloading = false;
  retrying = false;

  private pollTimer: ReturnType<typeof setInterval> | null = null;
  safePdfUrl: SafeResourceUrl | null = null;

  constructor(
    private readonly dialogRef: MatDialogRef<OrderPdfDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: OrderPdfDialogData,
    private readonly orderService: OrderService,
    private readonly sanitizer: DomSanitizer,
    private readonly toastr: ToastrService
  ) {}

  ngOnInit(): void {
    this.loadPdf();
  }

  ngOnDestroy(): void {
    this.stopPolling();
  }

  get orderLabel(): string {
    const number = this.data.orderNumber;
    return number != null && String(number).trim() ? `N° ${number}` : 'Orden de Servicio';
  }

  get showRetryAction(): boolean {
    return this.state === 'error' && this.errorAction === 'retry';
  }

  get showRefreshAction(): boolean {
    return this.state === 'error' && this.errorAction === 'refresh';
  }

  downloadPdf(): void {
    if (this.downloading) {
      return;
    }

    this.downloading = true;

    this.orderService
      .createOrderPdfDownloadUrl(this.data.orderId, 'attachment')
      .subscribe({
        next: (result) => {
          this.downloading = false;

          if (
            normalizePdfStatus(result?.pdfStatus) === OrderPdfStatus.GENERATED &&
            result?.url
          ) {
            triggerAttachmentDownload(result.url);
            this.toastr.success('Descarga iniciada.');
            return;
          }

          if (normalizePdfStatus(result?.pdfStatus) === OrderPdfStatus.FAILED) {
            this.logPdfDevError(result);
            const msg = this.resolvePdfFailedMessage(result);
            this.toastr.error(msg.title, formatUserMessageText(msg));
            return;
          }

          this.toastr.error(
            'Descarga no disponible',
            'El PDF aún no está listo para descargar.'
          );
        },
        error: (error: unknown) => {
          this.downloading = false;
          const msg = resolveOrderGraphqlUserMessage(
            error,
            'No se pudo descargar el PDF.'
          );
          this.toastr.error(msg.title, formatUserMessageText(msg));
        },
      });
  }

  close(): void {
    const status = normalizePdfStatus(this.data.pdfStatus ?? null);
    const result: OrderPdfDialogResult | undefined =
      status && status !== OrderPdfStatus.PENDING && status !== OrderPdfStatus.PROCESSING
        ? {
            pdfStatus: status,
            pdfGeneratedAt:
              status === OrderPdfStatus.GENERATED
                ? new Date().toISOString()
                : null,
            lastPdfError: this.data.lastPdfError ?? null,
          }
        : undefined;

    this.dialogRef.close(result);
  }

  retry(): void {
    if (this.retrying) {
      return;
    }

    if (this.errorAction === 'refresh') {
      this.loadPdf();
      return;
    }

    this.retrying = true;
    this.state = 'loading';
    this.errorTitle = null;
    this.errorMessage = null;
    this.errorAction = 'none';
    this.safePdfUrl = null;
    this.stopPolling();

    this.orderService.retryGenerateOrderPdf(this.data.orderId).subscribe({
      next: (result) => {
        this.retrying = false;
        this.data.pdfStatus = result.pdfStatus;
        this.data.lastPdfError = null;
        this.loadPdf();
      },
      error: (error: unknown) => {
        this.retrying = false;
        const msg = resolveOrderGraphqlUserMessage(
          error,
          'No se pudo reintentar la generación del PDF.'
        );
        this.showUserError(msg);
      },
    });
  }

  private loadPdf(): void {
    this.state = 'loading';
    this.errorTitle = null;
    this.errorMessage = null;
    this.errorAction = 'none';
    this.requestPdfUrl();
  }

  private requestPdfUrl(): void {
    this.orderService.createOrderPdfDownloadUrl(this.data.orderId, 'inline').subscribe({
      next: (result) => this.applyPdfResult(result),
      error: (error: unknown) => this.onRequestError(error),
    });
  }

  private applyPdfResult(result: OrderPdfDownloadUrlResult | null | undefined): void {
    if (!result) {
      this.onRequestError();
      return;
    }

    this.data.pdfStatus = result.pdfStatus;
    this.data.lastPdfError = result.lastPdfError ?? null;

    const status = normalizePdfStatus(result.pdfStatus);

    switch (status) {
      case OrderPdfStatus.PENDING:
      case OrderPdfStatus.PROCESSING:
        this.state = 'generating';
        this.startPolling();
        return;

      case OrderPdfStatus.GENERATED:
        if (result.url) {
          this.showPdf(result.url);
          return;
        }
        this.showUserError(
          resolveUserMessage(null, [], 'El PDF está listo pero no se recibió la URL.')
        );
        return;

      case OrderPdfStatus.FAILED:
        this.logPdfDevError(result);
        this.showUserError(this.resolvePdfFailedMessage(result));
        return;

      default:
        this.showUserError(
          resolveUserMessage(null, [], 'Estado del PDF desconocido.')
        );
    }
  }

  private resolvePdfFailedMessage(result: OrderPdfDownloadUrlResult): UserMessage {
    return resolveUserMessage(
      result.errorCode ?? 'PDF_GENERATION_FAILED',
      [],
      undefined,
      result.userMessage
    );
  }

  private logPdfDevError(result: OrderPdfDownloadUrlResult): void {
    logOrderDevDetail('pdf.lastPdfError', result.lastPdfError);
    logOrderDevDetail('pdf.errorCode', result.errorCode);
  }

  private showPdf(url: string): void {
    this.stopPolling();
    this.safePdfUrl = this.sanitizer.bypassSecurityTrustResourceUrl(url);
    this.state = 'view';
    this.errorTitle = null;
    this.errorMessage = null;
    this.errorAction = 'none';
    this.data.pdfStatus = OrderPdfStatus.GENERATED;
  }

  private showUserError(message: UserMessage): void {
    this.stopPolling();
    this.state = 'error';
    this.errorTitle = message.title;
    this.errorMessage = formatUserMessageText(message);
    this.errorAction = message.action ?? 'none';
    this.safePdfUrl = null;
  }

  private onRequestError(error?: unknown): void {
    const msg = resolveOrderGraphqlUserMessage(
      error,
      'No se pudo consultar el estado del PDF.'
    );
    this.showUserError(msg);
  }

  private startPolling(): void {
    if (this.pollTimer) {
      return;
    }

    this.pollTimer = setInterval(() => {
      this.orderService.createOrderPdfDownloadUrl(this.data.orderId, 'inline').subscribe({
        next: (result) => {
          if (!isPdfStatusGenerating(result?.pdfStatus)) {
            this.applyPdfResult(result);
          }
        },
        error: (error: unknown) => this.onRequestError(error),
      });
    }, ORDER_PDF_POLL_INTERVAL_MS);
  }

  private stopPolling(): void {
    if (this.pollTimer) {
      clearInterval(this.pollTimer);
      this.pollTimer = null;
    }
  }
}
